import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {
  selectBenefitsEligibility,
  selectWorkforceRecruitment,
  selectBenefitsPermissions,
  selectWorkforcePermissions,
  selectAddedFilters
} from '../slices/filterManagementSlice'
import {selectFilterConfig} from '../slices/configSlice'
import {convertOutreachToQueryFormat, getFilterConfig} from '../../utils/filterManagementHelpers'
import tooltipTexts from '../../components/common/TooltipText.json'

/**
 * Hook for outreach simulation filters (Benefits Eligibility and Workforce Recruitment)
 */
export const useOutreachFilters = () => {
  const benefitsEligibility = useSelector(selectBenefitsEligibility)
  const workforceRecruitment = useSelector(selectWorkforceRecruitment)
  const filterConfig = useSelector(selectFilterConfig)
  const benefitsPermissions = useSelector(selectBenefitsPermissions)
  const workforcePermissions = useSelector(selectWorkforcePermissions)
  const addedFilters = useSelector(selectAddedFilters)

  const isOutreachActive = useMemo(() => {
    const hasBenefits = benefitsEligibility?.selected?.length > 0
    const hasWorkforce = workforceRecruitment?.selected?.length > 0
    return hasBenefits || hasWorkforce
  }, [benefitsEligibility?.selected, workforceRecruitment?.selected])

  const activeOutreachType = useMemo(() => {
    if (benefitsEligibility?.selected?.length > 0) return 'benefits'
    if (workforceRecruitment?.selected?.length > 0) return 'workforce'
    return null
  }, [benefitsEligibility?.selected, workforceRecruitment?.selected])

  const activeOutreachKey = useMemo(() => {
    if (benefitsEligibility?.selected?.length > 0) return 'receivingBenefits'
    if (workforceRecruitment?.selected?.length > 0) return 'workforceRecruitment'
    return null
  }, [benefitsEligibility?.selected, workforceRecruitment?.selected])

  const activeOutreachValue = useMemo(() => {
    if (benefitsEligibility?.selected?.length > 0) return benefitsEligibility
    if (workforceRecruitment?.selected?.length > 0) return workforceRecruitment
    return null
  }, [benefitsEligibility, workforceRecruitment])

  const enrolledValue = useMemo(() => {
    if (benefitsEligibility?.selected?.length > 0) {
      return benefitsEligibility.enrolledValue ?? 2
    }
    if (workforceRecruitment?.selected?.length > 0) {
      return workforceRecruitment.enrolledValue ?? 2
    }
    return 2
  }, [benefitsEligibility, workforceRecruitment])

  const selectedOptions = useMemo(() => {
    if (benefitsEligibility?.selected?.length > 0) {
      return benefitsEligibility.selected
    }
    if (workforceRecruitment?.selected?.length > 0) {
      return workforceRecruitment.selected
    }
    return []
  }, [benefitsEligibility?.selected, workforceRecruitment?.selected])

  const outreachParams = useMemo(() => {
    if (!activeOutreachKey || !activeOutreachValue) {
      return {type: null, key: null, data: []}
    }

    const config = getFilterConfig(activeOutreachKey, filterConfig)
    if (!config?.options) {
      return {type: activeOutreachType, key: activeOutreachKey, data: []}
    }

    const data = convertOutreachToQueryFormat(activeOutreachValue, config.options)

    return {
      type: activeOutreachType,
      key: activeOutreachKey,
      data
    }
  }, [activeOutreachKey, activeOutreachValue, activeOutreachType, filterConfig])

  const activeFilterOptions = useMemo(() => {
    if (!activeOutreachKey) return []

    const config = getFilterConfig(activeOutreachKey, filterConfig)
    return config?.options || []
  }, [activeOutreachKey, filterConfig])

  const selectedOptionsWithConfig = useMemo(() => {
    if (!selectedOptions.length || !activeFilterOptions.length) return []

    return selectedOptions.map((value) => activeFilterOptions.find((opt) => opt.value === value)).filter(Boolean)
  }, [selectedOptions, activeFilterOptions])

  const benefitsOptionsWithState = useMemo(() => {
    const benefitsConfig = getFilterConfig('receivingBenefits', filterConfig)
    if (!benefitsConfig?.options) return []

    return benefitsConfig.options.map((option) => {
      const hasPermission = benefitsPermissions.includes(option.id)
      const mutuallyExclusiveKey = option.mutuallyExclusiveWith
      const isEligibilityFilterAdded = mutuallyExclusiveKey && addedFilters.includes(mutuallyExclusiveKey)
      const isDisabled = !hasPermission || isEligibilityFilterAdded

      let disabledReason = null
      if (!hasPermission) {
        disabledReason = tooltipTexts.filterDisabled
      } else if (isEligibilityFilterAdded) {
        disabledReason = tooltipTexts.benefitsAlreadySelected
      }

      return {
        ...option,
        hasPermission,
        isEnabled: hasPermission && !isEligibilityFilterAdded,
        isDisabled,
        disabledReason
      }
    })
  }, [filterConfig, benefitsPermissions, addedFilters])

  const workforceOptionsWithState = useMemo(() => {
    const workforceConfig = getFilterConfig('workforceRecruitment', filterConfig)
    if (!workforceConfig?.options) return []

    return workforceConfig.options.map((option) => {
      const hasPermission = workforcePermissions.includes(option.id)

      return {
        ...option,
        hasPermission,
        isEnabled: hasPermission,
        isDisabled: !hasPermission,
        disabledReason: hasPermission ? null : tooltipTexts.filterDisabled
      }
    })
  }, [filterConfig, workforcePermissions])

  return {
    isOutreachActive,
    activeOutreachType,
    enrolledValue,
    selectedOptions,
    selectedOptionsWithConfig,
    activeFilterOptions,
    benefitsOptionsWithState,
    workforceOptionsWithState,
    outreachParams
  }
}

export default useOutreachFilters
